import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Network, X, Plus } from 'lucide-react';
import { EntitySelectorDialog, EntityOption } from './EntitySelectorDialog';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface EntityLinkSelectorProps {
  allowedTypes: EntityType[];
  onLinksChange: (entities: SelectedEntity[]) => void;
  initialLinks?: SelectedEntity[];
}

// Standard linkable types in proper order
export const STANDARD_LINKABLE_TYPES: EntityType[] = [
  // Assets
  'site', 'zone', 'system', 'endpoint', 'cloud_resource', 'internet_connection', 'dmz_config',
  // Governance entities (sidebar order)
  'task', 'control', 'risk', 'incident', 'supplier', 'evidence'
];

const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  site: 'Sites',
  zone: 'Zones',
  system: 'Systems',
  endpoint: 'Endpoints',
  cloud_resource: 'Cloud Resources',
  internet_connection: 'Internet Connections',
  dmz_config: 'DMZ Configs',
  task: 'Tasks',
  control: 'Controls',
  risk: 'Risks',
  incident: 'Incidents',
  supplier: 'Suppliers',
  evidence: 'Evidence',
  asset: 'Assets',
  cloud: 'Cloud Services',
};

export function EntityLinkSelector({
  allowedTypes,
  onLinksChange,
  initialLinks = [],
}: EntityLinkSelectorProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedEntities, setSelectedEntities] = useState<SelectedEntity[]>(initialLinks);

  // Sync with initialLinks when they change
  useEffect(() => {
    setSelectedEntities(initialLinks);
  }, [initialLinks]);

  const handleSelectEntities = (entityType: EntityType, entities: EntityOption[]) => {
    // Add new entities with their actual names
    const newEntities = entities.map(e => ({
      id: e.id,
      type: entityType,
      name: e.name,
    }));
    
    // Filter out duplicates
    const existingIds = new Set(selectedEntities.map(e => e.id));
    const uniqueNewEntities = newEntities.filter(e => !existingIds.has(e.id));
    
    const updated = [...selectedEntities, ...uniqueNewEntities];
    setSelectedEntities(updated);
    onLinksChange(updated);
  };

  const handleRemoveEntity = (entityId: string) => {
    const updated = selectedEntities.filter(e => e.id !== entityId);
    setSelectedEntities(updated);
    onLinksChange(updated);
  };

  const groupedEntities = selectedEntities.reduce((acc, entity) => {
    if (!acc[entity.type]) {
      acc[entity.type] = [];
    }
    acc[entity.type].push(entity);
    return acc;
  }, {} as Record<EntityType, SelectedEntity[]>);

  return (
    <>
      <Separator />
      
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-muted-foreground" />
          <h4 className="text-sm font-medium">Link to Related Entities (Optional)</h4>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setSelectorOpen(true)}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Links
        </Button>

        {Object.keys(groupedEntities).length > 0 && (
          <div className="space-y-2">
            {Object.entries(groupedEntities).map(([type, entities]) => (
              <div key={type}>
                <p className="text-xs text-muted-foreground mb-1">
                  {ENTITY_TYPE_LABELS[type as EntityType] || type}:
                </p>
                <div className="flex flex-wrap gap-1">
                  {entities.map(entity => (
                    <Badge key={entity.id} variant="secondary" className="gap-1 pr-1">
                      <span className="truncate max-w-[150px]">{entity.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveEntity(entity.id)}
                        className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <EntitySelectorDialog
        open={selectorOpen}
        onOpenChange={setSelectorOpen}
        onSelect={handleSelectEntities}
        allowedTypes={allowedTypes}
        multiSelect={true}
        title="Select Entities to Link"
      />
    </>
  );
}
